/* Retail facets use the product's published specifications, with conservative title fallbacks. */
(() => {
  'use strict';
  const clean = value => String(value || '').replace(/&nbsp;|\u00a0/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();
  const normal = value => clean(value).toLowerCase();
  const categoryKey = value => normal(value) === 'keyboard' ? 'keyboards' : normal(value);
  const definitions = [
    ['brand', 'Brand', null, ['brand', 'manufacturer']],
    ['screen_size', 'Screen size', /monitors/, ['screen size', 'display diagonal']],
    ['resolution', 'Resolution', /monitors|webcams/, ['maximum resolution', 'display resolution']],
    ['refresh_rate', 'Refresh rate', /monitors/, ['refresh rate', 'maximum refresh rate']],
    ['panel_type', 'Panel type', /monitors/, ['display type', 'panel type']],
    ['capacity', 'Storage capacity', /storage|ssds|hard drives/, ['storage capacity', 'ssd capacity', 'hdd capacity', 'capacity']],
    ['ram_capacity', 'Memory capacity', /memory|ram/, ['memory capacity', 'internal memory', 'memory size', 'capacity']],
    ['memory_type', 'Memory type', /memory|ram|graphics cards/, ['internal memory type', 'memory type']],
    ['memory_speed', 'Memory speed', /memory|ram/, ['memory speed', 'memory clock speed']],
    ['socket', 'Socket', /processors|motherboards|cpu coolers/, ['socket', 'processor socket', 'socket type', 'supported processor sockets', 'supported sockets']],
    ['chipset', 'Chipset', /motherboards/, ['motherboard chipset', 'chipset']],
    ['form_factor', 'Form factor', /motherboards|ssds|storage|power supplies/, ['motherboard form factor', 'form factor']],
    ['wattage', 'Power output', /power supplies/, ['total power', 'wattage', 'power output']],
    ['efficiency', 'Efficiency', /power supplies/, ['efficiency rating', '80 plus certification']],
    ['gpu', 'Graphics processor', /graphics cards/, ['graphics processor', 'gpu']],
    ['vram', 'Graphics memory', /graphics cards/, ['discrete graphics card memory', 'graphics card memory', 'graphics memory']],
    ['connection', 'Connection', /keyboards|mice|headsets|headphones|earphones|loudspeakers|microphones|webcams|controller|cables|wi-fi/, ['connectivity', 'connection type', 'connection', 'device interface', 'wireless technology']],
    ['interface', 'Interface', /storage|ssds|hard drives|monitors/, ['interface', 'video inputs']],
    ['keyboard_layout', 'Keyboard layout', /keyboard/, ['keyboard layout', 'keyboard language']],
    ['switch_type', 'Switch type', /keyboard/, ['keyboard switch type', 'switch type']],
    ['keyboard_size', 'Keyboard size', /keyboard/, ['keyboard form factor']],
    ['dpi', 'Mouse sensitivity', /mice/, ['maximum dpi', 'movement resolution']],
    ['cable_length', 'Cable length', /cables/, ['cable length', 'length']],
    ['cooling', 'Cooling type', /cpu coolers/, ['cooling type']],
    ['colour', 'Colour', /./, ['colour', 'color', 'product colour']]
  ].map(([id, title, categories, labels]) => ({ key: `spec_${id}`, id, title, categories, labels }));

  function extract(product) {
    const specs = new Map((product.specs || []).map(([label, value]) => [normal(label), clean(value).replace(/^:\s*/, '')]));
    const title = clean(product.title);
    const category = String(product.category || product.type || '').split('|').map(categoryKey).join('|');
    const result = {};
    for (const def of definitions) {
      if (def.categories && !def.categories.test(category)) continue;
      let value = def.labels.map(label => specs.get(label)).find(Boolean) || '';
      if (def.id === 'brand' && !value && !/^(sku central|elitepcs)$/i.test(product.vendor || '')) value = product.vendor;
      if (!value) {
        const patterns = {
          screen_size: /\b(\d{2}(?:\.\d+)?)\s*(?:["″]|inch(?:es)?\b|in\b)/i,
          resolution: /\b(\d{3,4})\s*[x×]\s*(\d{3,4})\b/i,
          refresh_rate: /\b(\d{2,3}(?:\.\d+)?)\s*Hz\b/i,
          capacity: /\b(\d+(?:\.\d+)?)\s*(TB|GB)\b/i,
          ram_capacity: /\b(\d+)\s*GB\b/i,
          memory_type: /\b(G?DDR[34567]X?)\b/i,
          memory_speed: /\b(\d{3,5})\s*(?:MHz|MT\/s)\b/i,
          socket: /\b(AM[45]|LGA\s*\d{4}|sTR5|TR4)\b/i,
          wattage: /\b(\d{3,4})\s*W(?:atts?)?\b/i,
          colour: /\b(Black|White|Grey|Gray|Silver|Pink|Blue|Red|Green|Beige|Brown|Purple|Orange|Yellow)\b/i
        };
        const match = patterns[def.id]?.exec(title);
        if (match) {
          value = def.id === 'resolution' ? `${match[1]} x ${match[2]}`
            : def.id === 'capacity' ? `${match[1]} ${match[2].toUpperCase()}`
            : def.id === 'ram_capacity' ? `${match[1]} GB`
            : def.id === 'refresh_rate' ? `${match[1]} Hz`
            : def.id === 'screen_size' ? `${match[1]} in`
            : def.id === 'wattage' ? `${match[1]} W`
            : def.id === 'memory_speed' ? `${match[1]} MT/s` : match[1];
        }
        if (def.id === 'connection') {
          value = ['Bluetooth', 'Wireless', 'Wired', 'USB', '3.5mm'].filter(name => {
            if (name === 'Wireless') return /\bwireless\b|\bRF\b/i.test(title);
            return new RegExp(`\\b${name.replace('.', '\\.')}\\b`, 'i').test(title);
          }).join(', ');
        }
      }
      value = clean(value);
      if (!value || /^(n\/a|unknown|not specified|none|-|default title)$/i.test(value)) continue;
      if (def.id === 'colour') value = value.replace(/gray/ig, 'Grey').replace(/\b\w/g, c => c.toUpperCase());
      if (def.id === 'screen_size') {
        const inches = value.match(/(?:\(|^)(\d{2}(?:\.\d+)?)\s*(?:in\b|inch|["″])/i);
        if (inches) value = `${Number(inches[1])} in`;
        else if (/^\d+(?:\.\d+)?\s*cm$/i.test(value)) value = `${Math.round(parseFloat(value) / 2.54 * 10) / 10} in`;
      }
      if (['capacity', 'ram_capacity', 'vram'].includes(def.id)) value = value.replace(/(\d)\s*(GB|TB)\b/ig, '$1 $2').toUpperCase();
      if (def.id === 'capacity' && /^\d+(?:\.\d+)? GB$/.test(value) && Number.parseFloat(value) % 1000 === 0) value = `${Number.parseFloat(value) / 1000} TB`;
      if (def.id === 'refresh_rate') value = value.replace(/\s*hz/ig, ' Hz');
      if (def.id === 'memory_type') value = value.toUpperCase();
      if (def.id === 'resolution') value = value.replace(/\s*[x×]\s*/i, ' x ').replace(/\s*pixels?$/i, '');
      if (def.id === 'socket') value = value.replace(/\bSocket\s*/ig, '').replace(/LGA\s+/ig, 'LGA');
      const multiple = ['connection', 'interface', 'colour', 'socket'].includes(def.id);
      result[def.key] = [...new Set((multiple ? value.split(/[,;|]+/) : [value]).map(clean).filter(Boolean))];
    }
    return result;
  }

  // This only changes which brand controls are visible. The original native
  // inputs remain the filter engine's source of truth, including hidden choices.
  function createBrandBrowser(group, choices, state = {}, idPrefix = 'catalog-brands') {
    const doc = group.ownerDocument;
    state.query = state.query || '';
    state.expanded = Boolean(state.expanded);
    choices.id = choices.id || `${idPrefix}-choices`;
    const field = doc.createElement('label');
    field.className = 'elite-catalog-brands__search';
    const label = doc.createElement('span'); label.textContent = 'Search brands';
    const search = doc.createElement('input');
    search.type = 'search'; search.placeholder = 'Search brands'; search.autocomplete = 'off';
    search.value = state.query; search.dataset.catalogBrandSearch = ''; search.dataset.mobileKey = 'brand-search';
    search.setAttribute('aria-controls', choices.id);
    field.append(label, search); group.insertBefore(field, choices);
    const empty = doc.createElement('p');
    empty.className = 'elite-catalog-brands__empty'; empty.textContent = 'No brands match your search.';
    empty.setAttribute('role', 'status'); empty.setAttribute('aria-live', 'polite');
    const toggle = doc.createElement('button');
    toggle.type = 'button'; toggle.className = 'elite-catalog-brands__toggle';
    toggle.dataset.catalogBrandToggle = ''; toggle.dataset.mobileKey = 'brand-toggle';
    toggle.setAttribute('aria-controls', choices.id);
    group.append(empty, toggle);
    const searchKey = value => normal(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    function refresh() {
      const query = searchKey(state.query);
      const labels = [...choices.children];
      let matches = 0;
      labels.forEach((option, index) => {
        const input = option.querySelector('input');
        const match = searchKey(input?.dataset.filterLabel || option.textContent).includes(query);
        if (match) matches++;
        option.hidden = !match || (!query && !state.expanded && index >= 12 && !input?.checked);
      });
      empty.hidden = matches > 0;
      toggle.hidden = Boolean(query) || labels.length <= 12;
      toggle.textContent = state.expanded ? 'Show fewer brands' : 'Show all brands';
      toggle.setAttribute('aria-expanded', String(state.expanded));
    }
    function reset() { state.query = ''; state.expanded = false; search.value = ''; refresh(); }
    search.addEventListener('input', () => { state.query = search.value; refresh(); });
    toggle.addEventListener('click', () => { state.expanded = !state.expanded; refresh(); });
    choices.addEventListener('change', refresh);
    refresh();
    return { refresh, reset };
  }

  function create(root) {
    const target = root.querySelector('[data-catalog-spec-filters]');
    if (!target) return { sync() {}, restore() {} };
    const controls = new Map();
    const element = (tag, className, text) => {
      const node = document.createElement(tag); node.className = className || '';
      if (text !== undefined) node.textContent = text; return node;
    };
    function choice(key, value, text) {
      const label = element('label', 'elite-catalog-facet__option elite-listing-filter-option');
      const input = element('input'); input.type = 'checkbox';
      input.dataset.collectionFilter = key; input.dataset.filterLabel = text; input.value = value;
      label.append(input, element('span', '', text)); return label;
    }
    for (const def of definitions) {
      const group = element('div', 'elite-filter-group elite-catalog-facet');
      group.dataset.catalogFacet = def.key; group.hidden = true;
      const heading = element('div', 'elite-catalog-facet__heading');
      const title = element('h3', '', def.title); title.dataset.catalogFacetTitle = '';
      title.id = root.dataset.collectionHandle + '-' + def.key;
      group.setAttribute('role', 'group'); group.setAttribute('aria-labelledby', title.id);
      const clear = element('button', 'elite-catalog-facet__clear', 'Clear'); clear.type = 'button';
      clear.setAttribute('aria-label', 'Clear ' + def.title.toLowerCase());
      const choices = element('div', 'elite-catalog-facet__options');
      clear.addEventListener('click', () => {
        const inputs = [...choices.querySelectorAll('input')];
        inputs.forEach(input => { input.checked = false; });
        inputs[0]?.dispatchEvent(new Event('change', { bubbles: true }));
      });
      heading.append(title, clear); group.append(heading, choices); target.append(group);
      const brandBrowser = def.id === 'brand' ? createBrandBrowser(group, choices, {}, title.id) : null;
      controls.set(def.key, { group, choices, clear, brandBrowser });
    }
    root.querySelector('[data-collection-clear]')?.addEventListener('click', () => controls.get('spec_brand')?.brandBrowser.reset());
    function restore() {
      const params = new URLSearchParams(window.location.search);
      controls.forEach(({ choices }, key) => {
        params.getAll(key).flatMap(value => value.split(',')).forEach(value => {
          if (value && ![...choices.querySelectorAll('input')].some(input => normal(input.value) === normal(value))) {
            choices.append(choice(key, normal(value), value));
          }
        });
      });
    }
    restore();
    let lastSignature = '';
    let lastCategory = null;
    function sync(cards, selected) {
      const category = selected.accessory_category?.[0] || '';
      if (lastCategory !== null && category !== lastCategory) controls.get('spec_brand')?.brandBrowser.reset();
      lastCategory = category;
      const categoryMatch = card => {
        const categories = String(card.dataset.filterAccessoryCategory || '').split('|').map(categoryKey);
        return !category || categories.includes(category) || category === 'storage' && categories.some(c => ['nvme ssds', 'sata ssds', 'hard drives'].includes(c));
      };
      const scoped = cards.filter(categoryMatch);
      const options = new Map();
      for (const def of definitions) {
        const values = new Map();
        if (!def.categories || category && def.categories.test(category)) {
          for (const card of scoped) {
            for (const value of card.__eliteCatalogFacets?.[def.key] || []) if (!values.has(normal(value))) values.set(normal(value), value);
          }
        }
        const capacityOrder = value => {
          const match = value.match(/^(\d+(?:\.\d+)?)\s*(GB|TB)$/i);
          return match ? Number(match[1]) * (match[2].toUpperCase() === 'TB' ? 1000 : 1) : Infinity;
        };
        options.set(def.key, [...values].sort((a, b) => (def.id === 'capacity' ? capacityOrder(a[1]) - capacityOrder(b[1]) : 0)
          || a[1].localeCompare(b[1], 'en', { numeric: true })));
        // Keep refinements valid when stock or product type changes.
        selected[def.key] = (selected[def.key] || []).filter(value => values.has(value));
      }
      const signature = JSON.stringify([category, [...options]]);
      if (signature !== lastSignature) {
        lastSignature = signature;
        controls.forEach(({ group, choices }, key) => {
          const active = choices.contains(document.activeElement) ? document.activeElement.value : null;
          choices.replaceChildren(...options.get(key).map(([value, label]) => choice(key, value, label)));
          group.hidden = !options.get(key).length;
          if (active && !group.hidden) [...choices.querySelectorAll('input')].find(input => input.value === active)?.focus({ preventScroll: true });
        });
      }
      controls.forEach(({ choices, clear, brandBrowser }, key) => {
        const inputs = [...choices.querySelectorAll('input')];
        inputs.forEach(input => { input.checked = (selected[key] || []).includes(input.value); });
        clear.disabled = !inputs.some(input => input.checked);
        brandBrowser?.refresh();
      });
      const hint = target.querySelector('[data-catalog-spec-hint]');
      if (hint) hint.hidden = Boolean(category);
      const all = root.querySelector('[data-catalog-category-all]');
      if (all) all.setAttribute('aria-pressed', String(!category));
    }
    return { sync, restore };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { extract, definitions, create, createBrandBrowser };
  if (typeof window !== 'undefined') { window.ElitePCS = window.ElitePCS || {}; window.ElitePCS.catalogFacets = { extract, create, definitions, createBrandBrowser }; }
})();
